/**
 * TenantContext — RW-544 Phase A.2 Tier 1
 *
 * Subscribes to the current user's tenantId custom claim and to the
 * matching tenants/{tenantId} config doc. Exposes `useTenant()` returning
 * { tenant, brand, compliance, integrations, dataParticipation, isLoading,
 *   error, tenantId }.
 *
 * Tier 1 scope is foundation only — this provider is mounted but no
 * existing component reads from it yet. Tier 2 will replace inlined
 * brand-token literals across src/ with `useTenant().brand.coral` etc.
 *
 * Per CLAUDE.md Rule 12, this file uses src/services/data.js (subscribe)
 * instead of importing firebase/firestore directly. Auth state comes
 * straight from the firebase.js handle since claims live on the Auth
 * token, not in Firestore.
 *
 * Pre-backfill fallback: if a signed-in user has no tenantId claim yet
 * (their token predates the Tier 1 backfill), we default to 'reve'.
 * This keeps the UI working during the brief window between deploy and
 * backfill completion. Tier 3 removes the fallback once every claim is
 * known to carry tenantId.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { onIdTokenChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { subscribe, _setCurrentTenantId } from '../services/data';

const DEFAULT_TENANT_ID = 'reve';

const TenantContext = createContext({
  tenantId: null,
  tenant: null,
  brand: null,
  compliance: null,
  integrations: null,
  dataParticipation: null,
  claimReady: false,
  isLoading: true,
  error: null,
});

export function TenantProvider({ children }) {
  const [tenantId, setTenantId] = useState(null);
  const [tenant, setTenant] = useState(null);
  // RW-1705: does the CURRENT token actually carry the tenantId claim? The
  // 'reve' fallback keeps tenantId populated during the claimless first-sign-in
  // window, but firestore.rules' inTenant() rejects reads made with a claimless
  // token — so listeners attached in that window die (permission-denied) and
  // the SDK never restarts them. claimReady flips false→true when the healed
  // token lands, giving subscribers a dep to re-attach on.
  const [claimReady, setClaimReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Step 1 — track the signed-in user's tenantId claim. onIdTokenChanged (not
  // onAuthStateChanged) so the RW-702 server-side claim heal + getIdToken(true)
  // re-runs this and claimReady sees the new claims (RW-1705).
  useEffect(() => {
    const unsub = onIdTokenChanged(auth, async (currentUser) => {
      if (!currentUser) {
        setTenantId(null);
        setTenant(null);
        setClaimReady(false);
        _setCurrentTenantId(null);
        setIsLoading(false);
        return;
      }
      try {
        const tokenResult = await currentUser.getIdTokenResult();
        const claimTenant = tokenResult?.claims?.tenantId;
        const resolved = claimTenant ? String(claimTenant) : DEFAULT_TENANT_ID;
        setTenantId(resolved);
        setClaimReady(!!claimTenant);
        // Register with the data layer so every create/setDocument/batch.set
        // auto-stamps tenantId on writes (RW-544 Tier 2).
        _setCurrentTenantId(resolved);
      } catch (e) {
        // Token read failed — fall back to default tenant so UI doesn't
        // hang. Tier 3 escalates this to a hard error.
        console.warn('[TenantContext] getIdTokenResult failed, using default tenant', e?.message);
        setTenantId(DEFAULT_TENANT_ID);
        _setCurrentTenantId(DEFAULT_TENANT_ID);
      }
    });
    return unsub;
  }, []);

  // Step 2 — subscribe to tenants/{tenantId} once the claim resolves.
  useEffect(() => {
    if (!tenantId) {
      setTenant(null);
      setIsLoading(false);
      return undefined;
    }
    setIsLoading(true);
    const unsub = subscribe(
      `tenants/${tenantId}`,
      (data) => {
        setTenant(data);
        setError(data ? null : new Error(`tenants/${tenantId} not found`));
        setIsLoading(false);
      },
      (err) => {
        // permission-denied is expected during sign-out / token transitions.
        if (err?.code !== 'permission-denied') {
          console.warn('[TenantContext] tenant subscribe failed', err?.message);
        }
        setError(err);
        setIsLoading(false);
      },
    );
    return unsub;
  }, [tenantId]);

  const value = useMemo(() => ({
    tenantId,
    tenant,
    brand: tenant?.brand || null,
    compliance: tenant?.compliance || null,
    // Integrations are a subcollection in the final shape — Tier 2 will
    // expose a separate hook (useTenantIntegration) that subscribes to
    // tenants/{tenantId}/integrations/{name}. Surface a placeholder here
    // so consumers don't break when Tier 2 lands.
    integrations: null,
    dataParticipation: tenant?.dataParticipation || null,
    claimReady,
    isLoading,
    error,
  }), [tenantId, tenant, claimReady, isLoading, error]);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}
